import type { User, AuthResponse } from 'src/contracts/auth';
import type { RegisterData } from 'src/features/auth/api/auth-api';

import { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { triggerBalanceRefresh } from 'src/utils/balance-refresh';
import { getPrevLocation, clearPrevLocation } from 'src/utils/location-storage';

import { useAuthStore } from 'src/stores/auth-store';
import { authApi } from 'src/features/auth/api/auth-api';
import { showNotification } from 'src/lib/notifications';

import {
  setTokenExpiration,
  handleExpiredToken,
  isTokenAboutToExpire,
} from 'src/auth/token-manager';

import { saveAuthToStorage, getAuthFromStorage, clearAuthFromStorage } from 'src/lib/auth-storage';

// Public routes that don't require authentication redirects
const PUBLIC_ROUTES = ['/', '/sign-in', '/register', '/forgot-password'];

interface UseAuthReturn {
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  isAuthenticated: boolean;
  user: AuthResponse['user'] | null;
  loading: boolean;
}

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const errorRecord = error as Record<string, unknown>;
    const {message} = errorRecord;
    if (typeof message === 'string') {
      try {
        const parsed = JSON.parse(message) as { message?: string };
        if (parsed?.message) {
          return parsed.message;
        }
      } catch {
        // ignore JSON parse errors and fall back to the original message
      }
      return message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Authentication failed';
}

export const useAuth = (): UseAuthReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const setAuthState = useAuthStore((state) => state.setAuth);
  const clearAuthState = useAuthStore((state) => state.clearAuth);

  // Check if current route is public
  const isPublicRoute = useCallback(() => {
    const { pathname } = location;
    // Check exact matches
    if (PUBLIC_ROUTES.includes(pathname)) return true;
    // Check for portfolio pages which should also be public
    if (pathname.startsWith('/portfolio/')) return true;
    return false;
  }, [location]);

  useEffect(() => {
    if (handleExpiredToken()) {
      clearAuthState();
      return;
    }

    const authData = getAuthFromStorage();
    if (authData?.token) {
      const authUser = authData.user;
      if (authUser) {
        setAuthState(authData.token, authUser, authData.roles || []);
      }
    } else {
      clearAuthState();
    }

    if (isTokenAboutToExpire()) {
      console.log('Token is about to expire, will be refreshed on next API call');
    }
  }, [clearAuthState, setAuthState]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (handleExpiredToken()) {
        clearAuthState();

        // Only redirect to sign-in from protected routes
        if (!isPublicRoute()) {
          showNotification('Your session has expired. Please login again.', 'warning');
          navigate('/sign-in');
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(intervalId);
  }, [clearAuthState, isPublicRoute, navigate, showNotification]);

  const handleAuthSuccess = useCallback(
    (response: AuthResponse) => {
      console.log('Auth success:', response);
      if (response.token) {
        const userData = response.data || response.user;
        let normalizedUser = userData || null;

        try {
          if (normalizedUser && typeof normalizedUser === 'object') {
            const userObj = normalizedUser as unknown as Record<string, unknown>;
            const hasBalance = 'balance' in userObj && userObj.balance !== undefined;

            if (hasBalance) {
              normalizedUser = {
                ...normalizedUser,
                balance: userObj.balance as number,
                balanceCurrency: (userObj.balanceCurrency as string | undefined) || 'USD',
              } as User;
            }
          }
        } catch (error) {
          console.error('Error storing user balance data:', error);
        }

        if (!normalizedUser) {
          showNotification('Authentication failed', 'error');
          return;
        }

        const normalizedResponse: AuthResponse = {
          ...response,
          data: normalizedUser,
          user: normalizedUser,
          roles: response.roles || [],
        };

        saveAuthToStorage(normalizedResponse);
        setTokenExpiration();
        setAuthState(normalizedResponse.token, normalizedUser, normalizedResponse.roles || []);

        // Refresh any cached authenticated data
        queryClient.invalidateQueries();

        // Trigger a balance refresh after successful login
        triggerBalanceRefresh();

        // Flag to show extension promo modal on dashboard after login
        localStorage.setItem('jj_show_extension_promo', 'true');

        // Check for previous location to redirect to
        const prevLocation = getPrevLocation();
        if (prevLocation && prevLocation !== '/') {
          clearPrevLocation();
          navigate(prevLocation);
        } else {
          navigate('/dashboard'); // Navigate to dashboard instead of home page
        }

        window.location.reload();
        showNotification('Login successful', 'success');
      } else {
        showNotification('Authentication failed', 'error');
      }
    },
    [navigate, queryClient, setAuthState, showNotification]
  );

  const handleAuthError = useCallback(
    (error: unknown) => {
      console.error('Auth error:', error);
      showNotification(getErrorMessage(error), 'error');
    },
    [showNotification]
  );

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: handleAuthSuccess,
    onError: handleAuthError,
  });

  const registerMutation = useMutation({
    mutationFn: (data: RegisterData) => authApi.registerWithData(data),
    onSuccess: handleAuthSuccess,
    onError: handleAuthError,
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSuccess: (response) => {
      if (response.errorCode) {
        showNotification(response.message || 'Failed to send reset email', 'error');
        return;
      }
      showNotification('Password reset email sent successfully', 'success');
    },
    onError: handleAuthError,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ resetToken, newPassword }: { resetToken: string; newPassword: string }) =>
      authApi.resetPassword(resetToken, newPassword),
    onSuccess: (response) => {
      if (response.errorCode) {
        showNotification(response.message || 'Failed to reset password', 'error');
        return;
      }
      showNotification('Password reset successfully', 'success');
      navigate('/auth/login');
    },
    onError: handleAuthError,
  });

  const googleLoginMutation = useMutation({
    mutationFn: (credential: string) => authApi.googleLogin(credential),
    onSuccess: handleAuthSuccess,
    onError: handleAuthError,
  });

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        await loginMutation.mutateAsync({ email, password });
      } catch {
        // Error handling is done in mutation callbacks for backward compatibility.
      }
    },
    [loginMutation]
  );

  const register = useCallback(
    async (data: RegisterData) => {
      try {
        await registerMutation.mutateAsync(data);
      } catch {
        // Error handling is done in mutation callbacks for backward compatibility.
      }
    },
    [registerMutation]
  );

  const logout = useCallback(() => {
    clearAuthFromStorage();
    clearAuthState();
    localStorage.removeItem('token_expires_at');
    queryClient.clear();
    navigate('/sign-in');
  }, [clearAuthState, navigate, queryClient]);

  // Make logout function globally available for extension access
  useEffect(() => {
    (window as Window & { jobJourneyLogout?: () => void }).jobJourneyLogout = logout;

    // Cleanup on unmount
    return () => {
      delete (window as Window & { jobJourneyLogout?: () => void }).jobJourneyLogout;
    };
  }, [logout]);

  const forgotPassword = useCallback(
    async (email: string) => {
      try {
        await forgotPasswordMutation.mutateAsync(email);
      } catch {
        // Error handling is done in mutation callbacks for backward compatibility.
      }
    },
    [forgotPasswordMutation]
  );

  const resetPassword = useCallback(
    async (resetToken: string, newPassword: string) => {
      try {
        await resetPasswordMutation.mutateAsync({ resetToken, newPassword });
      } catch {
        // Error handling is done in mutation callbacks for backward compatibility.
      }
    },
    [resetPasswordMutation]
  );

  const googleLogin = useCallback(
    async (credential: string) => {
      try {
        await googleLoginMutation.mutateAsync(credential);
      } catch {
        // Error handling is done in mutation callbacks for backward compatibility.
      }
    },
    [googleLoginMutation]
  );

  const storageAuth = getAuthFromStorage();
  const resolvedUser = user || storageAuth?.user || null;

  return {
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    googleLogin,
    isAuthenticated: !!(token || storageAuth?.token),
    user: resolvedUser,
    loading:
      loginMutation.isPending ||
      registerMutation.isPending ||
      forgotPasswordMutation.isPending ||
      resetPasswordMutation.isPending ||
      googleLoginMutation.isPending,
  };
};
